import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useCallback } from 'use-memo-one'

import { AppDataTable } from '@root/features/app-data-table/app-data-table'
import { DataCounterButton } from '@root/shared/ui-kit'

import {
  alertsPanelShownAtom,
  trackingWalletAlertsTableModel,
  unseenAlertsCountAtom,
} from '../../atoms/reatom-tracking-wallets-alerts-table'

import type { Trade } from '@root/entities/trade'
import type { ClassNameProps } from '@root/shared/react'
import type { TableVirtuosoHandle } from 'react-virtuoso'

type WalletsAlertsTableProps = {} & ClassNameProps

const POSITIVE_OPERATIONS = ['buy', 'liquidityAdd', 'poolInit'] as const

export const WalletsAlertsTable: React.FC<WalletsAlertsTableProps> = ({
  className,
}) => {
  const model = trackingWalletAlertsTableModel

  const [paused, setPaused] = useAtom(trackingWalletAlertsTableModel.paused)
  const [scrolled, setScrolled] = useState(false)
  const [counter] = useAtom(unseenAlertsCountAtom)
  const flushBuffer = useAction(model.flushBuffer)
  const virtuosoRef = useRef<TableVirtuosoHandle>(null)
  const { t } = useTranslation()
  const ctx = useCtx()

  /**
   * On screen or not, said plainly.
   *
   * What arrives while this is false is held and counted on the badge in the
   * footer instead of going into a table nobody can see.
   */
  useEffect(() => {
    alertsPanelShownAtom(ctx, true)
    return () => {
      alertsPanelShownAtom(ctx, false)
    }
  }, [ctx])

  const getRowStyle = useCallback((row: Trade.Trade) => {
    const styles = {
      '--row-color': POSITIVE_OPERATIONS.includes(row.type)
        ? 'var(--up-9)'
        : 'var(--down-9)',
    } as React.CSSProperties

    return styles
  }, [])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop
    const passedThreshold = scrollTop > 20
    setScrolled(passedThreshold)
    if (passedThreshold) {
      setPaused(passedThreshold)
    }
  }

  const handleMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
    if (scrolled) {
      return
    }
    setPaused(false)
  }

  const handleClearAlertsAndScrollToTop = () => {
    flushBuffer()
    virtuosoRef.current?.scrollToIndex({
      index: 0,
      behavior: 'smooth',
    })
  }

  const floatingActions =
    paused && counter > 0 ? (
      <DataCounterButton
        counter={counter}
        text={t('wallet-tracker.new-alerts')}
        onClick={handleClearAlertsAndScrollToTop}
      />
    ) : undefined

  return (
    <Root onMouseOver={() => setPaused(true)} onMouseLeave={handleMouseLeave}>
      <AppDataTable
        virtuosoRef={virtuosoRef}
        floatingActions={floatingActions}
        getRowStyle={getRowStyle}
        className={className}
        rowHeight={40}
        model={model}
        onScroll={handleScroll}
      />
    </Root>
  )
}

const Root = styled.div`
  position: relative;
`
